/**
 * Copy, cut and delete: a passage lifted out of the score as a value.
 *
 * A clip is not a fragment of the document. It holds no ids that mean anything any more --
 * every event is given a fresh one when it is pasted -- and it addresses the marks on the
 * passage by position within itself rather than by the events they used to hang on. That is
 * what lets the same clip be pasted twice, into another part, or into another score
 * entirely, and still carry its dynamics and its slurs.
 *
 * A clip keeps one lane per part the selection covered, in score order, so that a passage
 * copied from three staves lands on three staves. Within a lane the events are the voice's
 * own, in time order.
 *
 * Pasting is in `clipboard-paste.ts` and re-exported from here, so that a caller has one
 * namespace for the whole gesture. That file imports only types from this one, so the two
 * halves do not form a cycle at runtime.
 */

import type { FiguredBass } from "../document/figures";
import type { Harmony } from "../document/harmony";
import { capacityOf, measureOnset, occupiedTicks } from "../document/query";
import type { EventLocation } from "../document/query";
import type { EventId, MeasureId, PartId } from "../document/ids";
import type { Direction, MusicEvent, Part, PartMeasure, Score, Spanner } from "../document/types";
import type { Command } from "../commands/types";
import { erase } from "./entry-voice";
import { selectedLocations } from "./selection";
import type { Selection } from "./selection";

export { paste } from "./clipboard-paste";

/** A direction, remembered by the position of the event it hangs on. */
export interface ClipDirection {
  readonly at: number;
  readonly direction: Direction;
}

/**
 * Something standing at a tick, remembered by the note it stands on and how far past it.
 *
 * A chord symbol and a stack of figures both stand at a tick rather than on a note, and the
 * bar they were copied from is not the bar they will be pasted into, so neither the tick nor
 * the bar can travel. What does travel is "over the third event of this lane, a quaver in",
 * which lands the mark in the right place however the passage is repositioned.
 */
export interface ClipMark<T> {
  readonly at: number;
  /** Ticks past the start of that event. */
  readonly offset: number;
  readonly mark: T;
}

export type ClipHarmony = ClipMark<Harmony>;
export type ClipFigures = ClipMark<FiguredBass>;

/** A slur or hairpin, remembered by the positions of its two ends. */
export interface ClipSpanner {
  readonly from: number;
  readonly to: number;
  readonly spanner: Spanner;
}

/** One part's worth of a copied passage. */
export interface ClipLane {
  readonly events: readonly MusicEvent[];
  readonly directions: readonly ClipDirection[];
  readonly spanners: readonly ClipSpanner[];
  readonly harmonies: readonly ClipHarmony[];
  readonly figures: readonly ClipFigures[];
}

/** A copied passage: one lane per part, and how long the longest of them lasts. */
export interface Clip {
  readonly lanes: readonly ClipLane[];
  readonly ticks: number;
}

export function isEmptyClip(clip: Clip): boolean {
  return clip.lanes.every((lane) => lane.events.length === 0);
}

/** One part's events as they are gathered, with where each one landed in the lane. */
interface Gathered {
  readonly events: MusicEvent[];
  readonly where: Map<EventId, number>;
  /** What each bar of the lane covers, for the symbols that stand over it. */
  readonly covered: Map<MeasureId, Span[]>;
  ticks: number;
}

/** One copied event's place in its own bar, and where it sits in the lane. */
interface Span {
  readonly at: number;
  readonly from: number;
  readonly to: number;
}

/**
 * Take the selection out of the score as a clip.
 *
 * Marks come with it when they belong to the passage: a dynamic whose note is in the clip,
 * a slur with both of its ends in the clip. A slur with one end outside is left behind,
 * because half a slur is not a slur -- and on paste it would have to point at a note the
 * clip does not contain.
 */
export function copy(score: Score, selection: Selection): Clip | string {
  const located = selectedLocations(score, selection);
  if (located.length === 0) return "nothing is selected";

  const order: PartId[] = [];
  const byPart = new Map<PartId, Gathered>();
  for (const at of located) {
    let lane = byPart.get(at.partId);
    if (lane === undefined) {
      lane = {
        events: [],
        where: new Map<EventId, number>(),
        covered: new Map<MeasureId, Span[]>(),
        ticks: 0,
      };
      byPart.set(at.partId, lane);
      order.push(at.partId);
    }
    const capacity = capacityOf(score, at.measureIndex);
    const from = at.onset - measureOnset(score, at.measureIndex);
    const spans = lane.covered.get(at.measureId) ?? [];
    spans.push({ at: lane.events.length, from, to: from + occupiedTicks(at.event, capacity) });
    lane.covered.set(at.measureId, spans);
    lane.where.set(at.event.id, lane.events.length);
    lane.events.push(at.event);
    lane.ticks += occupiedTicks(at.event, capacity);
  }

  const lanes: ClipLane[] = [];
  for (const partId of order) {
    const lane = byPart.get(partId);
    const part = score.parts.find((candidate) => candidate.id === partId);
    if (lane === undefined || part === undefined) continue;
    lanes.push({
      events: lane.events,
      directions: directionsIn(score, part, lane.where),
      spanners: spannersIn(part, lane.where),
      harmonies: marksIn(part, lane.covered, (measure) => measure.harmonies),
      figures: marksIn(part, lane.covered, (measure) => measure.figures),
    });
  }

  const ticks = [...byPart.values()].reduce((longest, lane) => Math.max(longest, lane.ticks), 0);
  return { lanes, ticks };
}

function directionsIn(
  score: Score,
  part: Part,
  where: ReadonlyMap<EventId, number>,
): readonly ClipDirection[] {
  const found: ClipDirection[] = [];
  for (const global of score.measures) {
    const measure = part.measures[global.id];
    if (measure === undefined) continue;
    for (const direction of measure.directions) {
      const at = where.get(direction.anchor);
      if (at !== undefined) found.push({ at, direction });
    }
  }
  return found;
}

/**
 * The marks standing at the ticks of the copied notes.
 *
 * One is taken when a copied note is sounding at the tick it stands on, which is the same
 * rule transposition uses: the mark belongs to the music under it, and half of a bar copied
 * brings the chords over that half and the figures under it.
 */
function marksIn<T extends { readonly onset: number }>(
  part: Part,
  covered: ReadonlyMap<MeasureId, readonly Span[]>,
  pick: (measure: PartMeasure) => readonly T[],
): readonly ClipMark<T>[] {
  const found: ClipMark<T>[] = [];
  for (const [measureId, spans] of covered) {
    const measure = part.measures[measureId];
    if (measure === undefined) continue;
    for (const mark of pick(measure)) {
      const span = spans.find((one) => mark.onset >= one.from && mark.onset < one.to);
      if (span !== undefined) found.push({ at: span.at, offset: mark.onset - span.from, mark });
    }
  }
  return found.sort((one, other) => one.at - other.at || one.offset - other.offset);
}

function spannersIn(part: Part, where: ReadonlyMap<EventId, number>): readonly ClipSpanner[] {
  const found: ClipSpanner[] = [];
  for (const spanner of part.spanners) {
    const from = where.get(spanner.from);
    const to = where.get(spanner.to);
    if (from !== undefined && to !== undefined) found.push({ from, to, spanner });
  }
  return found;
}

/** The selection grouped by the voice it sits in, since a voice is rewritten as a whole. */
function byVoice(located: readonly EventLocation[]): readonly (readonly EventLocation[])[] {
  const groups = new Map<string, EventLocation[]>();
  for (const at of located) {
    const key = [at.partId, at.measureId, at.voiceIndex].join(" ");
    groups.set(key, [...(groups.get(key) ?? []), at]);
  }
  return [...groups.values()];
}

/**
 * Silence the selection, leaving the bars as long as they were.
 *
 * Deleting notes is not deleting time. A bar that was four beats long is still four beats
 * long afterwards, with rests where the notes were, which is the only behaviour that keeps
 * every bar after the caret where the player expects it.
 */
export function remove(score: Score, selection: Selection): Command | string {
  const located = selectedLocations(score, selection);
  if (located.length === 0) return "nothing is selected";

  const commands: Command[] = [];
  for (const group of byVoice(located)) {
    const first = group[0];
    if (first === undefined) continue;
    const part = score.parts.find((candidate) => candidate.id === first.partId);
    const measure = part?.measures[first.measureId];
    let voice = measure?.voices[first.voiceIndex];
    if (part === undefined || measure === undefined || voice === undefined) continue;

    const capacity = capacityOf(score, first.measureIndex);
    // Highest index first, so the ones still to come keep the indices they were found at.
    const indices = group.map((at) => at.eventIndex).sort((one, other) => other - one);
    for (const index of indices) {
      const written = erase(voice, index, capacity);
      if (typeof written === "string") return written;
      voice = { ...voice, events: written.events };
    }

    const rewritten = voice;
    commands.push({
      kind: "setPartMeasure",
      partId: part.id,
      measureId: first.measureId,
      measure: {
        ...measure,
        voices: measure.voices.map((held, at) => (at === first.voiceIndex ? rewritten : held)),
      },
    });
  }

  if (commands.length === 0) return "there is nothing there to delete";
  return { kind: "batch", label: "delete", commands };
}
